import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Inbox as InboxIcon, MessageSquare, Mail, Send, Plus, Search, ChevronLeft, ChevronRight,
  Phone, X, Loader2, AlertTriangle, User as UserIcon, Zap, Star, Paperclip,
  ArrowDownUp, FileText, ChevronDown, Trash2, RotateCcw,
} from "lucide-react";
import { toast } from "sonner";
import { inboxApi, customersApi } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";

function timeOf(iso) {
  try { return new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }); } catch { return ""; }
}
function relOf(iso) {
  try {
    const d = new Date(iso); const now = new Date();
    const same = d.toDateString() === now.toDateString();
    return same ? timeOf(iso) : d.toLocaleDateString([], { month: "short", day: "numeric" });
  } catch { return ""; }
}
function dayOf(iso) {
  try {
    const d = new Date(iso), today = new Date();
    return d.toDateString() === today.toDateString() ? "Today" : d.toLocaleDateString([], { weekday: "short", month: "short", day: "numeric" });
  } catch { return ""; }
}
const initials = (n) => (n || "?").trim().split(/\s+/).slice(0, 2).map((s) => s[0]?.toUpperCase()).join("");

export default function Inbox({ api = inboxApi, platformMode = false }) {
  const { user } = useAuth();
  const isAdmin = platformMode ? true : user?.role === "admin";
  // Trash/restore is an admin + dispatcher action; technicians read and send only.
  const canTrash = platformMode || ["admin", "dispatcher"].includes(user?.role);
  const [searchParams, setSearchParams] = useSearchParams();

  const [settings, setSettings] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [composeOpen, setComposeOpen] = useState(false);
  // Sort: newest (default), oldest, currentUser (green first), unread (unread first), important (starred first).
  const [sortBy, setSortBy] = useState("newest");
  // Only one email preview popup open at a time, per active thread.
  const [expandedMsgId, setExpandedMsgId] = useState(null);
  // Sidebar pagination — 15 rows per page.
  const PAGE_SIZE = 15;
  const [page, setPage] = useState(1);
  // Delete confirmations (conversation from sidebar, single message from thread).
  const [deleteConvTarget, setDeleteConvTarget] = useState(null);
  const [deletingConv, setDeletingConv] = useState(false);
  const [deleteMsgTarget, setDeleteMsgTarget] = useState(null);
  const [deletingMsg, setDeletingMsg] = useState(false);
  // Trash tab — soft-deleted conversations/messages, restorable (Gmail untrash).
  const [view, setView] = useState("inbox");
  const [trash, setTrash] = useState({ conversations: [], messages: [] });
  const [restoringId, setRestoringId] = useState(null);
  // Message "selected for reply" — drives per-message reply targeting + subject autofill.
  const [selectedMsg, setSelectedMsg] = useState(null);

  const scrollRef = useRef(null);
  const lastTsRef = useRef(null);

  const loadConversations = useCallback(async () => {
    const cs = await api.conversations();
    setConversations(cs);
    return cs;
  }, [api]);

  useEffect(() => {
    api.settings().then(setSettings).catch(() => setSettings({}));
    loadConversations().then((cs) => {
      const qid = searchParams.get("c");
      const isDesktop = typeof window !== "undefined" && window.matchMedia("(min-width: 768px)").matches;
      const target = cs.find((c) => c.id === qid) || (isDesktop ? cs[0] : null);
      if (target) setActiveId(target.id);
    }).catch(() => {});
    if (searchParams.get("compose") === "1") setComposeOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // refresh conversation list periodically
  useEffect(() => {
    const id = setInterval(() => { loadConversations().catch(() => {}); }, 8000);
    return () => clearInterval(id);
  }, [loadConversations]);

  const loadThread = useCallback(async (convId) => {
    setLoading(true);
    try {
      const [det, msgs] = await Promise.all([
        api.getConversation(convId),
        api.messages(convId, { limit: 300 }),
      ]);
      setDetail(det);
      setMessages(msgs);
      lastTsRef.current = msgs.length ? msgs[msgs.length - 1].createdAt : null;
      await api.markRead(convId);
      setConversations((prev) => prev.map((c) => (c.id === convId ? { ...c, unread: 0 } : c)));
      window.dispatchEvent(new Event("inbox-changed"));
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (!activeId) return;
    setMessages([]);
    setExpandedMsgId(null);
    setSelectedMsg(null);
    loadThread(activeId);
    setSearchParams((p) => { const n = new URLSearchParams(p); n.set("c", activeId); return n; }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId]);

  // poll new messages in the open conversation
  useEffect(() => {
    if (!activeId) return;
    const id = setInterval(async () => {
      try {
        const after = lastTsRef.current;
        const fresh = await api.messages(activeId, after ? { after } : { limit: 300 });
        if (fresh.length) {
          setMessages((prev) => {
            const seen = new Set(prev.map((m) => m.id));
            const add = fresh.filter((m) => !seen.has(m.id));
            return add.length ? [...prev, ...add] : prev;
          });
          lastTsRef.current = fresh[fresh.length - 1].createdAt;
          await api.markRead(activeId);
          loadConversations().catch(() => {});
          window.dispatchEvent(new Event("inbox-changed"));
        }
      } catch { /* ignore */ }
    }, 4000);
    return () => clearInterval(id);
  }, [activeId, loadConversations, api]);

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, activeId]);

  const active = conversations.find((c) => c.id === activeId) || detail;
  const totalUnread = conversations.reduce((s, c) => s + (c.unread || 0), 0);
  const trashCount = trash.conversations.length + trash.messages.length;

  // Filter by search, then sort by the selected mode.
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const searched = conversations.filter(
      (c) => !q ||
        (c.contactName || "").toLowerCase().includes(q) ||
        (c.contactPhone || "").includes(search) ||
        (c.contactEmail || "").toLowerCase().includes(q)
    );
    const byNewest = (a, b) => (b.lastMessageAt || "").localeCompare(a.lastMessageAt || "");
    const byOldest = (a, b) => (a.lastMessageAt || "").localeCompare(b.lastMessageAt || "");
    const sorted = [...searched];
    if (sortBy === "oldest") sorted.sort(byOldest);
    else if (sortBy === "currentUser") sorted.sort((a, b) => Number(!!b.isCurrentUser) - Number(!!a.isCurrentUser) || byNewest(a, b));
    else if (sortBy === "unread") sorted.sort((a, b) => Number(!!b.unread) - Number(!!a.unread) || byNewest(a, b));
    else if (sortBy === "important") sorted.sort((a, b) => Number(!!b.important) - Number(!!a.important) || byNewest(a, b));
    else sorted.sort(byNewest); // newest first (default)
    return sorted;
  }, [conversations, search, sortBy]);

  // Reset to page 1 whenever the search term or sort order changes.
  useEffect(() => { setPage(1); }, [search, sortBy]);
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  useEffect(() => { setPage((p) => Math.min(p, totalPages)); }, [totalPages]);
  const paged = useMemo(() => filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE), [filtered, page]);

  const toggleStar = useCallback(async (conv, e) => {
    if (e) { e.preventDefault(); e.stopPropagation(); }
    const next = !conv.important;
    // Optimistic UI update — the API call is fire-and-forget with a rollback on failure.
    setConversations((prev) => prev.map((c) => c.id === conv.id ? { ...c, important: next } : c));
    try {
      await api.star(conv.id, next);
    } catch (err) {
      setConversations((prev) => prev.map((c) => c.id === conv.id ? { ...c, important: conv.important } : c));
      toast.error(err?.response?.data?.detail || "Could not update star");
    }
  }, [api]);

  const onSent = (msg) => {
    setMessages((prev) => [...prev, msg]);
    lastTsRef.current = msg.createdAt;
    loadConversations().catch(() => {});
  };

  const onComposed = async ({ conversationId }) => {
    setComposeOpen(false);
    await loadConversations();
    setActiveId(conversationId);
    toast.success("Message sent");
  };

  const loadTrash = useCallback(async () => {
    try { setTrash(await api.trash()); } catch { /* ignore */ }
  }, [api]);

  useEffect(() => { if (view === "trash") loadTrash(); }, [view, loadTrash]);

  const confirmDeleteConv = async () => {
    if (!deleteConvTarget) return;
    setDeletingConv(true);
    try {
      const res = await api.deleteConversation(deleteConvTarget.id);
      setConversations((prev) => prev.filter((c) => c.id !== deleteConvTarget.id));
      if (activeId === deleteConvTarget.id) {
        setActiveId(null);
        setDetail(null);
        setMessages([]);
      }
      toast.success(res?.gmail ? `Moved to Trash — ${res.gmail} email${res.gmail > 1 ? "s" : ""} moved to Gmail Trash` : "Moved to Trash");
      if (res?.failed) toast.error(`${res.failed} email(s) could not be moved in Gmail`);
      window.dispatchEvent(new Event("inbox-changed"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not move conversation to Trash");
    } finally {
      setDeletingConv(false);
      setDeleteConvTarget(null);
    }
  };

  const confirmDeleteMsg = async () => {
    if (!deleteMsgTarget || !activeId) return;
    setDeletingMsg(true);
    try {
      const res = await api.deleteMessage(activeId, deleteMsgTarget.id);
      setMessages((prev) => prev.filter((m) => m.id !== deleteMsgTarget.id));
      toast.success(res?.gmail ? "Moved to Gmail Trash" : "Moved to Trash");
      if (res?.failed) toast.error("Could not move this email in Gmail");
      loadConversations().catch(() => {});
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not move message to Trash");
    } finally {
      setDeletingMsg(false);
      setDeleteMsgTarget(null);
    }
  };

  const restoreConv = async (c) => {
    setRestoringId(c.id);
    try {
      await api.restoreConversation(c.id);
      setTrash((prev) => ({ ...prev, conversations: prev.conversations.filter((x) => x.id !== c.id) }));
      await loadConversations();
      toast.success("Conversation restored");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not restore conversation");
    } finally { setRestoringId(null); }
  };

  const restoreMsg = async (m) => {
    setRestoringId(m.id);
    try {
      await api.restoreMessage(m.conversationId, m.id);
      setTrash((prev) => ({ ...prev, messages: prev.messages.filter((x) => x.id !== m.id) }));
      await loadConversations();
      if (activeId === m.conversationId) loadThread(activeId);
      toast.success(m.channel === "email" ? "Email restored" : "Message restored");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not restore message");
    } finally { setRestoringId(null); }
  };

  return (
    <div className="flex-1 min-h-0 flex bg-white" data-testid="inbox-page">
      {/* Conversation list */}
      <aside className={`w-full md:w-80 shrink-0 min-h-0 border-r border-slate-200 flex flex-col bg-slate-50/60 ${activeId ? "hidden md:flex" : "flex"}`}>
        <div className="px-4 py-3.5 border-b border-slate-200">
          <div className="flex items-center justify-between">
            <h1 className="text-[16px] font-bold text-slate-900 flex items-center gap-2">
              <InboxIcon className="h-[18px] w-[18px] text-blue-500" /> Inbox
            </h1>
            <button onClick={() => setComposeOpen(true)} data-testid="inbox-compose-btn" className="relative h-8 pl-3 pr-3.5 gap-1.5 rounded-md bg-blue-600 text-white flex items-center hover:bg-blue-700 text-[12.5px] font-semibold">
              <Plus className="h-4 w-4" /> New
              {totalUnread > 0 && (
                <span data-testid="inbox-new-btn-unread" className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white ring-2 ring-slate-50 text-[10px] font-bold flex items-center justify-center">
                  {totalUnread > 99 ? "99+" : totalUnread}
                </span>
              )}
            </button>
          </div>
          <div className="relative mt-2.5">
            <Search className="h-3.5 w-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={platformMode ? "Search inbox" : "Search customers"} data-testid="inbox-search" className="w-full h-8 pl-8 pr-2 rounded-md border border-slate-200 bg-white text-slate-800 text-[12.5px] placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20" />
          </div>
          {/* Inbox / Trash tabs — Trash holds soft-deleted items until restored */}
          <div className="mt-2.5 flex items-center gap-1 p-0.5 rounded-md bg-slate-200/70" data-testid="inbox-view-tabs">
            {[["inbox", "Inbox", InboxIcon], ["trash", "Trash", Trash2]].map(([key, label, Icon]) => (
              <button
                key={key}
                onClick={() => setView(key)}
                data-testid={`inbox-view-tab-${key}`}
                className={`flex-1 h-7 rounded-[5px] flex items-center justify-center gap-1.5 text-[12px] font-semibold transition-colors ${
                  view === key ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"
                }`}
              >
                <Icon className="h-3.5 w-3.5" /> {label}
                {key === "trash" && trashCount > 0 && (
                  <span className="min-w-[16px] h-[16px] px-1 rounded-full bg-slate-400 text-white text-[9px] font-bold flex items-center justify-center" data-testid="inbox-trash-count">{trashCount}</span>
                )}
              </button>
            ))}
          </div>
          {view === "inbox" && (<>
          <div className="mt-2 flex items-center gap-1.5">
            <ArrowDownUp className="h-3 w-3 text-slate-400" />
            <label className="text-[10.5px] font-semibold text-slate-500 uppercase tracking-wide">Sort</label>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              data-testid="inbox-sort-select"
              className="flex-1 h-7 rounded-md border border-slate-200 bg-white text-slate-800 text-[12px] px-2 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
            >
              <option value="newest">Newest to Oldest</option>
              <option value="oldest">Oldest to Newest</option>
              <option value="currentUser">Current User first</option>
              <option value="unread">Unread first</option>
              <option value="important">Important first</option>
            </select>
          </div>
          <div className="mt-2 flex items-center justify-between" data-testid="inbox-pagination">
            <span className="text-[11px] text-slate-500 font-medium" data-testid="inbox-pagination-label">Page {page} of {totalPages}</span>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                data-testid="inbox-pagination-prev"
                className="h-6 w-6 rounded-md flex items-center justify-center border border-slate-200 text-slate-600 disabled:opacity-30 disabled:cursor-not-allowed hover:bg-slate-100"
              >
                <ChevronLeft className="h-3.5 w-3.5" />
              </button>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                data-testid="inbox-pagination-next"
                className="h-6 w-6 rounded-md flex items-center justify-center border border-slate-200 text-slate-600 disabled:opacity-30 disabled:cursor-not-allowed hover:bg-slate-100"
              >
                <ChevronRight className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
          </>)}
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto">
          {view === "trash" ? (
            <TrashList
              trash={trash}
              canTrash={canTrash}
              restoringId={restoringId}
              onRestoreConv={restoreConv}
              onRestoreMsg={restoreMsg}
            />
          ) : (<>
          {filtered.length === 0 && (
            <div className="p-6 text-center text-[12.5px] text-slate-400" data-testid="inbox-empty-list">
              No conversations yet. Tap <span className="font-semibold text-slate-600">New</span> to message {platformMode ? "someone" : "a customer"}.
            </div>
          )}
          {paged.map((c) => (
            <div key={c.id} className={`group w-full text-left flex items-center gap-3 px-3.5 py-3 border-b border-slate-100 transition-colors cursor-pointer ${c.id === activeId ? "bg-blue-600 text-white" : "hover:bg-slate-100/70"}`} onClick={() => setActiveId(c.id)} data-testid={`inbox-conv-${c.id}`}>
              {/* Avatar — green when the sender is one of our own users */}
              <span
                data-testid={`inbox-conv-avatar-${c.id}`}
                className={`h-9 w-9 shrink-0 rounded-full flex items-center justify-center text-[12px] font-bold ${
                  c.id === activeId
                    ? "bg-white/20 text-white"
                    : c.isCurrentUser
                      ? "bg-emerald-500 text-white"
                      : "bg-slate-200 text-slate-600"
                }`}
                title={c.isCurrentUser ? "This sender is one of your users" : undefined}
              >
                {initials(c.contactName)}
              </span>
              <span className="flex-1 min-w-0">
                <span className="flex items-center justify-between gap-2">
                  <span className={`truncate text-[13.5px] ${c.unread ? "font-bold" : "font-semibold"} ${c.id === activeId ? "text-white" : "text-slate-800"}`}>{c.contactName}</span>
                  <span className={`shrink-0 text-[10.5px] ${c.id === activeId ? "text-white/70" : "text-slate-400"}`}>{relOf(c.lastMessageAt)}</span>
                </span>
                <span className="flex items-center gap-1.5 mt-0.5">
                  {c.lastChannel === "email"
                    ? <Mail className={`h-3 w-3 shrink-0 ${c.id === activeId ? "text-white/70" : "text-slate-400"}`} />
                    : <MessageSquare className={`h-3 w-3 shrink-0 ${c.id === activeId ? "text-white/70" : "text-slate-400"}`} />}
                  <span className={`truncate text-[12px] ${c.id === activeId ? "text-white/80" : "text-slate-500"}`}>
                    {c.lastDirection === "in" ? "" : "You: "}{c.lastMessagePreview || "No messages"}
                  </span>
                  {c.unread > 0 && (
                    <span className="ml-auto shrink-0 min-w-[16px] h-[16px] px-1 rounded-full bg-rose-500 text-white text-[9px] font-bold flex items-center justify-center" data-testid={`inbox-conv-unread-${c.id}`}>{c.unread}</span>
                  )}
                </span>
              </span>
              {/* Star toggle — always visible so users can mark important */}
              <button
                onClick={(e) => toggleStar(c, e)}
                data-testid={`inbox-conv-star-${c.id}`}
                title={c.important ? "Unmark as important" : "Mark as important"}
                className={`shrink-0 h-7 w-7 rounded-full flex items-center justify-center transition-colors ${
                  c.id === activeId ? "hover:bg-white/20" : "hover:bg-slate-200/70"
                }`}
              >
                <Star
                  className={`h-4 w-4 ${
                    c.important
                      ? "fill-rose-500 text-rose-500"
                      : c.id === activeId ? "text-white/60" : "text-slate-300 group-hover:text-slate-400"
                  }`}
                />
              </button>
              {/* Move to Trash — shown on hover (desktop) or always (touch) */}
              {canTrash && (
              <button
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); setDeleteConvTarget(c); }}
                data-testid={`inbox-conv-delete-${c.id}`}
                title="Move conversation to Trash"
                className={`shrink-0 h-7 w-7 rounded-full flex items-center justify-center transition-opacity opacity-100 md:opacity-0 md:group-hover:opacity-100 ${
                  c.id === activeId ? "hover:bg-white/20" : "hover:bg-rose-50"
                }`}
              >
                <Trash2 className={`h-3.5 w-3.5 ${c.id === activeId ? "text-white/70" : "text-slate-400 hover:text-rose-500"}`} />
              </button>
              )}
            </div>
          ))}
          </>)}
        </div>
      </aside>

      {/* Thread pane */}
      <main data-testid="inbox-thread-pane" className={`min-w-0 min-h-0 flex flex-col bg-[#0A0F1C] fixed inset-0 z-40 md:static md:z-auto md:flex-1 ${activeId && view === "inbox" ? "flex" : "hidden md:flex"}`}>
        {view === "trash" ? (
          <div className="flex-1 flex items-center justify-center px-8 text-center" data-testid="inbox-trash-hint">
            <p className="max-w-sm text-[13px] leading-relaxed text-slate-400">
              Items here are not deleted. Emails sent or received through a connected Gmail mailbox sit in your
              <span className="text-slate-200 font-semibold"> Gmail Trash</span> — restoring one brings it back to both places.
            </p>
          </div>
        ) : !active ? (
          <div className="flex-1 flex items-center justify-center text-slate-400 text-[13px]">Select a conversation</div>
        ) : (
          <>
            <ThreadHeader conv={active} detail={detail} onBack={() => setActiveId(null)} onToggleStar={toggleStar} />
            <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto px-4 md:px-8 py-5 space-y-1" data-testid="inbox-message-list">
              {loading && messages.length === 0 && <div className="text-center text-slate-400 text-[12px] py-10">Loading…</div>}
              {!loading && messages.length === 0 && <div className="text-center text-slate-400 text-[12px] py-10">No messages yet — start the conversation below.</div>}
              <MessageStream
                messages={messages}
                canTrash={canTrash}
                expandedId={expandedMsgId}
                onToggleExpand={(id) => setExpandedMsgId((prev) => (prev === id ? null : id))}
                onDeleteMessage={setDeleteMsgTarget}
                selectedMsgId={selectedMsg?.id}
                onSelectMessage={(m) => setSelectedMsg((prev) => (prev?.id === m.id ? null : m))}
              />
            </div>
            <Composer
              key={activeId}
              conv={active}
              settings={settings}
              isAdmin={isAdmin}
              api={api}
              selectedMsg={selectedMsg}
              onClearSelectedMsg={() => setSelectedMsg(null)}
              onSent={(msg) => { setSelectedMsg(null); onSent(msg); }}
              onSimulated={async (msg) => {
                setMessages((prev) => [...prev, msg]);
                lastTsRef.current = msg.createdAt;
                try { await api.markRead(activeId); } catch { /* ignore */ }
                loadConversations().catch(() => {});
                window.dispatchEvent(new Event("inbox-changed"));
              }}
            />
          </>
        )}
      </main>

      {composeOpen && (
        <ComposeModal
          settings={settings}
          api={api}
          platformMode={platformMode}
          onClose={() => setComposeOpen(false)}
          onComposed={onComposed}
          initialCustomer={searchParams.get("customerId") ? { id: searchParams.get("customerId"), name: searchParams.get("customerName") || "" } : null}
        />
      )}

      {deleteConvTarget && (
        <ConfirmDeleteModal
          message="Move this conversation to Trash? Each email in it is moved to your Gmail Trash individually — Gmail has no thread-level trash — and texts are hidden from the inbox. Nothing is deleted permanently; restore it any time from the Trash tab."
          deleting={deletingConv}
          onCancel={() => setDeleteConvTarget(null)}
          onConfirm={confirmDeleteConv}
        />
      )}

      {deleteMsgTarget && (
        <ConfirmDeleteModal
          message={deleteMsgTarget.channel === "email"
            ? "Move this email to Trash? Only this one message moves to your Gmail Trash — the rest of the conversation stays. You can restore it from the Trash tab."
            : "Move this text to Trash? It is hidden from the conversation and can be restored from the Trash tab."}
          deleting={deletingMsg}
          onCancel={() => setDeleteMsgTarget(null)}
          onConfirm={confirmDeleteMsg}
        />
      )}
    </div>
  );
}

function ConfirmDeleteModal({ message, deleting, onCancel, onConfirm }) {
  return (
    <div className="fixed inset-0 z-[70] bg-black/50 flex items-center justify-center p-4" onClick={onCancel} data-testid="inbox-delete-confirm-modal">
      <div className="w-full max-w-sm rounded-xl bg-white shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 flex items-start gap-3">
          <span className="h-9 w-9 rounded-full bg-rose-50 flex items-center justify-center shrink-0">
            <AlertTriangle className="h-4.5 w-4.5 text-rose-500" />
          </span>
          <p className="text-[13.5px] text-slate-700 leading-relaxed pt-1.5" data-testid="inbox-delete-confirm-message">{message}</p>
        </div>
        <div className="px-5 py-3.5 border-t border-slate-100 flex justify-end gap-2 bg-slate-50">
          <button onClick={onCancel} data-testid="inbox-delete-cancel-btn" className="h-9 px-4 rounded-md border border-slate-200 text-slate-700 text-[12.5px] font-semibold hover:bg-slate-100">
            Cancel
          </button>
          <button onClick={onConfirm} disabled={deleting} data-testid="inbox-delete-confirm-btn" className="h-9 px-4 rounded-md bg-rose-600 text-white text-[12.5px] font-semibold hover:bg-rose-700 disabled:opacity-50 flex items-center gap-1.5">
            {deleting && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Move to Trash
          </button>
        </div>
      </div>
    </div>
  );
}

function TrashList({ trash, canTrash, restoringId, onRestoreConv, onRestoreMsg }) {
  const empty = trash.conversations.length === 0 && trash.messages.length === 0;
  if (empty) {
    return (
      <div className="p-6 text-center text-[12.5px] text-slate-400" data-testid="inbox-trash-empty">
        Trash is empty. Items you move here stay recoverable.
      </div>
    );
  }
  const restoreBtn = (item, onRestore, testid) => (
    canTrash ? (
      <button
        onClick={() => onRestore(item)}
        disabled={restoringId === item.id}
        data-testid={testid}
        title="Restore to Inbox"
        className="shrink-0 h-7 px-2.5 rounded-md border border-emerald-200 bg-emerald-50 text-emerald-700 text-[11.5px] font-semibold flex items-center gap-1 hover:bg-emerald-100 disabled:opacity-50"
      >
        {restoringId === item.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <RotateCcw className="h-3 w-3" />} Restore
      </button>
    ) : null
  );
  return (
    <div data-testid="inbox-trash-list">
      {trash.conversations.map((c) => (
        <div key={c.id} className="flex items-center gap-3 px-3.5 py-3 border-b border-slate-100" data-testid={`inbox-trash-conv-${c.id}`}>
          <span className="h-9 w-9 shrink-0 rounded-full bg-slate-200 text-slate-500 flex items-center justify-center text-[12px] font-bold">
            {initials(c.contactName)}
          </span>
          <span className="flex-1 min-w-0">
            <span className="block truncate text-[13.5px] font-semibold text-slate-700">{c.contactName}</span>
            <span className="block truncate text-[11.5px] text-slate-400">
              Whole conversation · {c.trashedByName ? `by ${c.trashedByName} · ` : ""}{relOf(c.trashedAt)}
            </span>
          </span>
          {restoreBtn(c, onRestoreConv, `inbox-trash-restore-conv-${c.id}`)}
        </div>
      ))}
      {trash.messages.map((m) => (
        <div key={m.id} className="flex items-center gap-3 px-3.5 py-3 border-b border-slate-100" data-testid={`inbox-trash-msg-${m.id}`}>
          <span className="h-9 w-9 shrink-0 rounded-full bg-slate-100 text-slate-400 flex items-center justify-center">
            {m.channel === "email" ? <Mail className="h-4 w-4" /> : <MessageSquare className="h-4 w-4" />}
          </span>
          <span className="flex-1 min-w-0">
            <span className="block truncate text-[13.5px] font-semibold text-slate-700">{m.subject || m.body || "(No content)"}</span>
            <span className="block truncate text-[11.5px] text-slate-400">
              {m.channel === "email" ? "Single email" : "Single text"} · {m.contactName}
              {m.gmailTrashed ? " · in Gmail Trash" : ""}
            </span>
          </span>
          {restoreBtn(m, onRestoreMsg, `inbox-trash-restore-msg-${m.id}`)}
        </div>
      ))}
    </div>
  );
}

function ThreadHeader({ conv, detail, onBack, onToggleStar }) {
  return (
    <div className="h-16 shrink-0 border-b border-white/10 px-4 md:px-6 flex items-center gap-3 bg-[#0B1730]">
      <button onClick={onBack} data-testid="inbox-back" className="md:hidden h-8 w-8 rounded-md hover:bg-white/10 flex items-center justify-center text-slate-300"><ChevronLeft className="h-5 w-5" /></button>
      <span
        data-testid="inbox-thread-avatar"
        className={`h-10 w-10 rounded-full flex items-center justify-center text-[13px] font-bold ${
          conv.isCurrentUser ? "bg-emerald-500 text-white" : "bg-blue-500/20 text-blue-200"
        }`}
        title={conv.isCurrentUser ? "This sender is one of your users" : undefined}
      >
        {initials(conv.contactName)}
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <div className="text-[15px] font-bold text-white truncate" data-testid="inbox-thread-name">{conv.contactName}</div>
          {conv.isCurrentUser && (
            <span data-testid="inbox-thread-current-user" className="shrink-0 inline-flex items-center gap-1 px-2 h-5 rounded-full bg-emerald-500/20 border border-emerald-400/40 text-emerald-300 text-[10px] font-bold uppercase tracking-wide">
              <UserIcon className="h-2.5 w-2.5" /> Current user
            </span>
          )}
        </div>
        <div className="flex items-center gap-3 text-[11.5px] text-slate-400 truncate">
          {conv.contactPhone && <span className="flex items-center gap-1"><Phone className="h-3 w-3" />{conv.contactPhone}</span>}
          {conv.contactEmail && <span className="flex items-center gap-1"><Mail className="h-3 w-3" />{conv.contactEmail}</span>}
        </div>
      </div>
      <button
        onClick={(e) => onToggleStar && onToggleStar(conv, e)}
        data-testid="inbox-thread-star"
        title={conv.important ? "Unmark as important" : "Mark as important"}
        className="h-8 w-8 rounded-md flex items-center justify-center hover:bg-white/10 transition-colors"
      >
        <Star className={`h-4 w-4 ${conv.important ? "fill-rose-500 text-rose-500" : "text-slate-400"}`} />
      </button>
    </div>
  );
}

function MessageStream({ messages, canTrash, expandedId, onToggleExpand, onDeleteMessage, selectedMsgId, onSelectMessage }) {
  let lastDay = null;
  return (
    <>
      {messages.map((m) => {
        const day = dayOf(m.createdAt);
        const showDay = day !== lastDay; lastDay = day;
        return (
          <React.Fragment key={m.id}>
            {showDay && (
              <div className="flex items-center gap-3 my-3">
                <div className="flex-1 h-px bg-white/10" />
                <span className="text-[10.5px] font-semibold text-slate-500 uppercase tracking-wide">{day}</span>
                <div className="flex-1 h-px bg-white/10" />
              </div>
            )}
            <MessageBubble
              m={m}
              canTrash={canTrash}
              expanded={expandedId === m.id}
              onToggleExpand={onToggleExpand}
              onDelete={onDeleteMessage}
              selected={selectedMsgId === m.id}
              onSelect={onSelectMessage}
            />
          </React.Fragment>
        );
      })}
    </>
  );
}

function MessageBubble({ m, canTrash, expanded, onToggleExpand, onDelete, selected, onSelect }) {
  const out = m.direction === "out";
  const isEmail = m.channel === "email";
  const hasSubject = isEmail && !!m.subject;
  const failed = m.status === "failed";
  const bubble = out
    ? (failed ? "bg-rose-900/40 border border-rose-500/50 text-rose-50" : "bg-blue-600 text-white rounded-br-md")
    : "bg-[#16203A] border border-[#389ECF]/45 text-slate-100 rounded-bl-md";
  const deleteBtn = canTrash ? (
    <button
      onClick={() => onDelete && onDelete(m)}
      data-testid={`inbox-msg-delete-${m.id}`}
      title={isEmail ? "Move this email to Trash" : "Move this text to Trash"}
      className="shrink-0 mt-1 h-6 w-6 rounded-full flex items-center justify-center transition-opacity opacity-100 md:opacity-0 md:group-hover:opacity-100 hover:bg-rose-500/20 text-slate-500 hover:text-rose-400"
    >
      <Trash2 className="h-3 w-3" />
    </button>
  ) : null;
  return (
    <div className="flex flex-col" data-testid={`inbox-msg-${m.id}`}>
      <div className={`group flex items-start gap-1.5 ${out ? "justify-end" : "justify-start"}`}>
        {out && deleteBtn}
        <div
          onClick={() => onSelect && onSelect(m)}
          data-testid={`inbox-msg-select-${m.id}`}
          title={`Reply to this ${isEmail ? "email" : "text"}`}
          className={`max-w-[80%] md:max-w-[62%] rounded-2xl px-3.5 py-2.5 cursor-pointer transition-shadow ${bubble} ${
            selected ? "ring-2 ring-amber-400 ring-offset-2 ring-offset-[#0A0F1C]" : ""
          }`}
        >
          <div className="flex items-center gap-1.5 mb-1">
            <span className={`inline-flex items-center gap-1 text-[9.5px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded ${out ? "bg-white/20 text-white" : "bg-blue-500/15 text-blue-200"}`}>
              {isEmail ? <Mail className="h-2.5 w-2.5" /> : <MessageSquare className="h-2.5 w-2.5" />}
              {isEmail ? "Email" : "SMS"}
            </span>
            {m.direction === "in" && <span className="text-[9.5px] font-semibold uppercase tracking-wide text-slate-400">Customer</span>}
            {selected && <span className="ml-auto text-[9px] font-bold uppercase tracking-wide text-amber-400">Replying</span>}
          </div>
          {hasSubject && (
            <button
              onClick={(e) => { e.stopPropagation(); onToggleExpand(m.id); }}
              data-testid={`inbox-email-subject-${m.id}`}
              className={`w-full flex items-center justify-between gap-2 text-left mb-1 rounded-md -mx-0.5 px-0.5 py-0.5 hover:opacity-80 transition-opacity ${out ? "text-white" : "text-slate-50"}`}
              title={expanded ? "Collapse email preview" : "Expand full email"}
            >
              <span className="text-[13px] font-bold break-words whitespace-normal">{m.subject}</span>
              <ChevronDown className={`h-3.5 w-3.5 shrink-0 transition-transform ${expanded ? "rotate-180" : ""}`} />
            </button>
          )}
          <div className={`text-[13.5px] leading-relaxed break-words ${out ? "text-white" : "text-slate-100"} ${hasSubject ? "truncate" : "whitespace-pre-wrap"}`}>
            {hasSubject ? (m.body || "").split("\n")[0] : m.body}
          </div>
          {(m.mediaUrls || []).length > 0 && (
            <div className="mt-2 flex flex-col gap-1" data-testid={`inbox-msg-attachments-${m.id}`}>
              {m.mediaUrls.map((u, i) => {
                const fname = decodeURIComponent(String(u).split("/").pop() || `Attachment ${i + 1}`);
                return (
                  <a
                    key={i}
                    href={u}
                    target="_blank"
                    rel="noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className={`inline-flex items-center gap-1.5 px-2 py-1.5 rounded-md text-[11.5px] font-semibold truncate max-w-full ${
                      out ? "bg-white/15 text-white hover:bg-white/25" : "bg-slate-700/60 text-slate-100 hover:bg-slate-700"
                    }`}
                  >
                    <Paperclip className="h-3 w-3 shrink-0" />
                    <span className="truncate">{fname}</span>
                  </a>
                );
              })}
            </div>
          )}
          <div className={`flex items-center gap-1.5 mt-1 text-[10px] ${out ? "text-white/60 justify-end" : "text-slate-400"}`}>
            {failed && <span className="flex items-center gap-1 text-rose-300 font-semibold"><AlertTriangle className="h-3 w-3" /> Failed</span>}
            {out && m.createdByName && !failed && <span>{m.createdByName}</span>}
            <span>{timeOf(m.createdAt)}</span>
          </div>
          {failed && m.error && <div className="mt-1 text-[10.5px] text-rose-300">{m.error}</div>}
        </div>
        {!out && deleteBtn}
      </div>
      {hasSubject && expanded && (
        <div className="flex justify-start mt-1.5" data-testid={`inbox-email-preview-${m.id}`}>
          <div className="max-w-[80%] md:max-w-[62%] w-full ml-3 rounded-xl border border-[#389ECF]/40 bg-[#0B1730] shadow-xl overflow-hidden">
            <div className="flex items-center justify-between px-3 py-1.5 border-b border-white/10 bg-[#16203A]">
              <span className="text-[10.5px] font-bold uppercase tracking-wide text-blue-300">Full message</span>
              <button
                onClick={() => onToggleExpand(m.id)}
                data-testid={`inbox-email-preview-close-${m.id}`}
                title="Close preview"
                className="h-5 w-5 rounded flex items-center justify-center hover:bg-white/10 text-slate-300"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
            <div className="px-3 py-2 text-[12.5px] leading-[1.6] text-slate-200 whitespace-pre-wrap break-words overflow-y-auto" style={{ height: "104px" }}>
              {m.body || <span className="text-slate-500">(No content)</span>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Composer({ conv, settings, isAdmin, api, onSent, onSimulated, selectedMsg, onClearSelectedMsg }) {
  const canSms = !!conv.contactPhone;
  const canEmail = !!conv.contactEmail;
  const [channel, setChannel] = useState(canSms ? "sms" : "email");
  const [body, setBody] = useState("");
  const [subject, setSubject] = useState("");
  const [sending, setSending] = useState(false);
  const [attachments, setAttachments] = useState([]); // [{ id, url, filename, contentType, size }]
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => { setChannel(canSms ? "sms" : "email"); }, [conv.id, canSms]);
  // Reset attachments when the conversation changes.
  useEffect(() => { setAttachments([]); }, [conv.id]);

  // The exact address a selected message came from/went to — replies target
  // this instead of the conversation default once the channel matches it.
  const replyAddr = selectedMsg ? (selectedMsg.direction === "in" ? selectedMsg.fromAddr : selectedMsg.toAddr) : "";
  const replyTarget = selectedMsg && selectedMsg.channel === channel ? replyAddr : "";

  const smsConfigured = !!settings?.smsConfigured;
  const smsDisabled = !canSms || !smsConfigured;
  const smsTitle = !canSms ? "No phone number on file for this contact" : (!smsConfigured ? "SMS is not configured in Settings" : undefined);
  const notConfigured = (channel === "sms" && !smsConfigured) || (channel === "email" && !settings?.emailConfigured);

  const chooseSms = () => {
    if (!canSms || !smsConfigured) return;
    setChannel("sms");
  };
  const chooseEmail = () => {
    if (!canEmail) return;
    setChannel("email");
    if (selectedMsg && selectedMsg.channel === "email") {
      const subj = (selectedMsg.subject || "").trim();
      setSubject(subj ? (/^re:/i.test(subj) ? subj : `Re: ${subj}`) : "");
    }
  };

  const onFileChosen = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      const uploaded = [];
      for (const f of Array.from(files)) {
        const res = await api.upload(f);
        uploaded.push(res);
      }
      setAttachments((prev) => [...prev, ...uploaded]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const send = async () => {
    if (sending) return;
    if (!body.trim() && attachments.length === 0) return;
    setSending(true);
    try {
      const mediaUrls = attachments.map((a) => a.url);
      const msg = await api.send(conv.id, {
        channel, body: body.trim(), subject: subject.trim() || undefined, mediaUrls,
        to: replyTarget || undefined,
      });
      onSent(msg);
      setBody(""); setSubject(""); setAttachments([]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send");
    } finally { setSending(false); }
  };

  const simulate = async () => {
    if (!body.trim() || sending) return;
    const addr = channel === "sms" ? conv.contactPhone : conv.contactEmail;
    if (!addr) { toast.error(`No ${channel === "sms" ? "phone" : "email"} on this conversation`); return; }
    setSending(true);
    try {
      const res = await api.simulateInbound({ channel, from: addr, body: body.trim(), subject: subject.trim() || undefined });
      onSimulated(res.message);
      setBody(""); setSubject("");
      toast.success("Simulated inbound reply");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Simulate failed");
    } finally { setSending(false); }
  };

  return (
    <div className="shrink-0 border-t border-white/10 bg-[#0B1730] px-4 md:px-6 py-3">
      <div className="flex items-center gap-2 mb-2">
        <button onClick={chooseSms} disabled={smsDisabled} title={smsTitle} data-testid="inbox-channel-sms"
          className={`flex items-center gap-1.5 h-7 px-3 rounded-full text-[12px] font-semibold transition-colors disabled:opacity-30 ${channel === "sms" ? "bg-blue-600 text-white" : "bg-white/10 text-slate-300 hover:bg-white/15"}`}>
          <MessageSquare className="h-3.5 w-3.5" /> SMS
        </button>
        <button onClick={chooseEmail} disabled={!canEmail} data-testid="inbox-channel-email"
          className={`flex items-center gap-1.5 h-7 px-3 rounded-full text-[12px] font-semibold transition-colors disabled:opacity-30 ${channel === "email" ? "bg-blue-600 text-white" : "bg-white/10 text-slate-300 hover:bg-white/15"}`}>
          <Mail className="h-3.5 w-3.5" /> Email
        </button>
        {notConfigured && (
          <span className="text-[11px] text-amber-300 flex items-center gap-1"><AlertTriangle className="h-3 w-3" /> {channel === "sms" ? "Twilio" : "Email"} not configured in Settings</span>
        )}
      </div>
      {replyTarget && (
        <div className="flex items-center gap-1.5 mb-2 text-[11px] text-amber-300" data-testid="inbox-reply-target-chip">
          <span>Replying to <span className="font-semibold">{replyTarget}</span></span>
          <button onClick={onClearSelectedMsg} data-testid="inbox-reply-target-clear" title="Reply to the conversation's default address instead" className="h-4 w-4 rounded-full flex items-center justify-center hover:bg-white/10">
            <X className="h-3 w-3" />
          </button>
        </div>
      )}
      {channel === "email" && (
        <input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Subject" data-testid="inbox-subject-input"
          className="w-full h-9 px-3 mb-2 rounded-md bg-[#16203A] border border-white/10 text-[13px] text-slate-100 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500/30" />
      )}
      {/* Attachment chips */}
      {attachments.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-2" data-testid="inbox-composer-attachments">
          {attachments.map((a) => (
            <span key={a.id} className="inline-flex items-center gap-1.5 pl-2 pr-1 h-7 rounded-md bg-white/10 text-slate-100 text-[11.5px] font-semibold">
              <FileText className="h-3 w-3" />
              <span className="truncate max-w-[180px]">{a.filename}</span>
              <button
                onClick={() => setAttachments((prev) => prev.filter((x) => x.id !== a.id))}
                data-testid={`inbox-composer-attachment-remove-${a.id}`}
                className="h-5 w-5 rounded flex items-center justify-center hover:bg-white/10"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}
      <div className="flex items-end gap-2">
        <textarea value={body} onChange={(e) => setBody(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey && channel === "sms") { e.preventDefault(); send(); } }}
          rows={2} placeholder={`Write a ${channel === "sms" ? "text message" : "email"}…`} data-testid="inbox-composer-input"
          className="flex-1 resize-none rounded-md bg-[#16203A] border border-white/10 px-3 py-2 text-[13.5px] text-slate-100 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500/30 max-h-36 min-h-[44px]" />
        <div className="flex flex-col gap-1.5">
          <input ref={fileRef} type="file" multiple onChange={(e) => onFileChosen(e.target.files)} data-testid="inbox-composer-file-input" className="hidden" />
          <button
            onClick={() => fileRef.current?.click()}
            disabled={uploading || sending}
            title="Attach a document"
            data-testid="inbox-composer-attach-btn"
            className="h-8 px-2.5 rounded-md bg-white/10 text-slate-200 hover:bg-white/20 disabled:opacity-30 flex items-center gap-1 text-[11px] font-semibold"
          >
            {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Paperclip className="h-3.5 w-3.5" />}
            {uploading ? "Uploading" : "Attach"}
          </button>
          {isAdmin && (
            <button onClick={simulate} disabled={!body.trim() || sending} title="Inject an inbound reply (test)" data-testid="inbox-simulate-btn"
              className="h-8 px-2.5 rounded-md bg-white/10 text-slate-200 hover:bg-white/20 disabled:opacity-30 flex items-center gap-1 text-[11px] font-semibold">
              <Zap className="h-3.5 w-3.5" /> Simulate
            </button>
          )}
          <button onClick={send} disabled={(!body.trim() && attachments.length === 0) || sending} data-testid="inbox-send-btn"
            className="h-9 px-4 rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-40 flex items-center gap-1.5 text-[12.5px] font-semibold">
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send
          </button>
        </div>
      </div>
    </div>
  );
}

function ComposeModal({ settings, api, platformMode = false, onClose, onComposed, initialCustomer = null }) {
  const [channel, setChannel] = useState("sms");
  const [customers, setCustomers] = useState([]);
  const [q, setQ] = useState("");
  const [picked, setPicked] = useState(initialCustomer);
  const [manual, setManual] = useState("");
  const [name, setName] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  // Platform inbox has no "customers" — skip the fetch entirely.
  useEffect(() => {
    if (platformMode) return;
    customersApi.list().then(setCustomers).catch(() => setCustomers([]));
  }, [platformMode]);

  const results = useMemo(() => {
    if (platformMode || !q.trim()) return [];
    const s = q.toLowerCase();
    return customers.filter((c) =>
      (c.name || "").toLowerCase().includes(s) ||
      (c.email || "").toLowerCase().includes(s) ||
      (c.phone || "").includes(q)
    ).slice(0, 6);
  }, [q, customers, platformMode]);

  const onFileChosen = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      const uploaded = [];
      for (const f of Array.from(files)) {
        const res = await api.upload(f);
        uploaded.push(res);
      }
      setAttachments((prev) => [...prev, ...uploaded]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const submit = async () => {
    if (sending) return;
    if (!body.trim() && attachments.length === 0) return;
    const payload = { channel, body: body.trim(), subject: subject.trim() || undefined, mediaUrls: attachments.map((a) => a.url) };
    if (picked) payload.customerId = picked.id;
    else { payload.to = manual.trim(); payload.name = name.trim() || undefined; }
    if (!picked && !manual.trim()) {
      toast.error(platformMode ? "Enter a recipient" : "Choose a customer or enter a recipient");
      return;
    }
    setSending(true);
    try {
      const res = await api.compose(payload);
      onComposed(res);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send");
    } finally { setSending(false); }
  };

  // Theme classes: dark for platform mode, light for tenant mode.
  const dark = platformMode;
  const modalBg      = dark ? "bg-[#0B1730] text-slate-100" : "bg-white text-slate-900";
  const headerBorder = dark ? "border-white/10" : "border-slate-200";
  const closeBtn     = dark ? "hover:bg-white/10 text-slate-300" : "hover:bg-slate-100 text-slate-500";
  const titleText    = dark ? "text-white" : "text-slate-900";
  const labelText    = dark ? "text-slate-300" : "text-slate-600";
  const inputBase    = dark
    ? "bg-[#16203A] border-white/10 text-slate-100 placeholder:text-slate-500 focus:ring-blue-500/30"
    : "bg-white border-slate-200 text-slate-800 placeholder:text-slate-400 focus:ring-blue-500/20";
  const tabInactive  = dark ? "bg-white/10 text-slate-300 hover:bg-white/15" : "bg-slate-100 text-slate-600";
  const divider      = dark ? "bg-white/10" : "bg-slate-100";
  const pickedBg     = dark ? "border-blue-400/40 bg-blue-500/10" : "border-blue-200 bg-blue-50";
  const pickedName   = dark ? "text-slate-100" : "text-slate-800";
  const pickedMeta   = dark ? "text-slate-400" : "text-slate-500";
  const chipBg       = dark ? "bg-white/10 text-slate-100" : "bg-slate-100 text-slate-700";
  const cancelBtn    = dark ? "border-white/10 text-slate-200 hover:bg-white/10" : "border-slate-200 text-slate-600 hover:bg-slate-50";

  return (
    <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4" onClick={onClose} data-testid="inbox-compose-modal">
      <div className={`w-full max-w-lg rounded-2xl shadow-2xl overflow-hidden ${modalBg}`} onClick={(e) => e.stopPropagation()}>
        <div className={`px-5 py-4 border-b flex items-center justify-between ${headerBorder}`}>
          <h2 className={`text-[16px] font-bold ${titleText}`}>New message</h2>
          <button onClick={onClose} className={`h-8 w-8 rounded-md flex items-center justify-center ${closeBtn}`}><X className="h-4 w-4" /></button>
        </div>
        <div className="p-5 space-y-3.5 max-h-[70vh] overflow-y-auto">
          <div className="flex items-center gap-2">
            <button onClick={() => setChannel("sms")} data-testid="compose-channel-sms" className={`flex-1 h-9 rounded-md text-[12.5px] font-semibold flex items-center justify-center gap-1.5 ${channel === "sms" ? "bg-blue-600 text-white" : tabInactive}`}><MessageSquare className="h-4 w-4" /> SMS</button>
            <button onClick={() => setChannel("email")} data-testid="compose-channel-email" className={`flex-1 h-9 rounded-md text-[12.5px] font-semibold flex items-center justify-center gap-1.5 ${channel === "email" ? "bg-blue-600 text-white" : tabInactive}`}><Mail className="h-4 w-4" /> Email</button>
          </div>

          {picked ? (
            <div className={`flex items-center gap-2.5 p-2.5 rounded-lg border ${pickedBg}`} data-testid="compose-picked">
              <span className="h-8 w-8 rounded-full bg-blue-600 text-white flex items-center justify-center text-[11px] font-bold">{initials(picked.name)}</span>
              <div className="min-w-0 flex-1">
                <div className={`text-[13px] font-semibold truncate ${pickedName}`}>{picked.name}</div>
                <div className={`text-[11.5px] truncate ${pickedMeta}`}>{channel === "sms" ? picked.phone || "No phone" : picked.email || "No email"}</div>
              </div>
              <button onClick={() => setPicked(null)} className={`text-[12px] ${pickedMeta} hover:opacity-80`}>Change</button>
            </div>
          ) : (
            <>
              {!platformMode && (
                <>
                  <div>
                    <label className={`text-[12px] font-semibold ${labelText}`}>Find a customer</label>
                    <div className="relative mt-1">
                      <Search className="h-3.5 w-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
                      <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name, phone or email" data-testid="compose-customer-search" className={`w-full h-9 pl-8 pr-2 rounded-md border text-[13px] focus:outline-none focus:ring-2 ${inputBase}`} />
                    </div>
                    {results.length > 0 && (
                      <div className={`mt-1 border rounded-md overflow-hidden divide-y ${dark ? "border-white/10 divide-white/10" : "border-slate-200 divide-slate-100"}`}>
                        {results.map((c) => (
                          <button key={c.id} onClick={() => { setPicked(c); setQ(""); }} data-testid={`compose-customer-${c.id}`} className={`w-full flex items-center gap-2.5 px-2.5 py-2 text-left ${dark ? "hover:bg-white/10" : "hover:bg-slate-50"}`}>
                            <span className={`h-7 w-7 rounded-full flex items-center justify-center text-[10px] font-bold ${dark ? "bg-white/10 text-slate-200" : "bg-slate-200 text-slate-600"}`}>{initials(c.name)}</span>
                            <span className="min-w-0">
                              <span className={`block text-[12.5px] font-semibold truncate ${pickedName}`}>{c.name}</span>
                              <span className={`block text-[11px] truncate ${pickedMeta}`}>{channel === "sms" ? c.phone : c.email}</span>
                            </span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className={`flex items-center gap-2 text-[11px] ${pickedMeta}`}><div className={`flex-1 h-px ${divider}`} /> or enter manually <div className={`flex-1 h-px ${divider}`} /></div>
                </>
              )}
              <div className="grid grid-cols-2 gap-2">
                <input value={manual} onChange={(e) => setManual(e.target.value)} placeholder={channel === "sms" ? "+15551234567" : "name@email.com"} data-testid="compose-manual-to" className={`h-9 px-3 rounded-md border text-[13px] focus:outline-none focus:ring-2 ${inputBase}`} />
                <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Name (optional)" data-testid="compose-manual-name" className={`h-9 px-3 rounded-md border text-[13px] focus:outline-none focus:ring-2 ${inputBase}`} />
              </div>
            </>
          )}

          {channel === "email" && (
            <input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Subject" data-testid="compose-subject" className={`w-full h-9 px-3 rounded-md border text-[13px] focus:outline-none focus:ring-2 ${inputBase}`} />
          )}
          <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={4} placeholder={`Write your ${channel === "sms" ? "text" : "email"}…`} data-testid="compose-body" className={`w-full resize-none rounded-md border px-3 py-2 text-[13.5px] focus:outline-none focus:ring-2 ${inputBase}`} />

          {/* Attachment chips */}
          {attachments.length > 0 && (
            <div className="flex flex-wrap gap-1.5" data-testid="compose-attachments">
              {attachments.map((a) => (
                <span key={a.id} className={`inline-flex items-center gap-1.5 pl-2 pr-1 h-7 rounded-md text-[11.5px] font-semibold ${chipBg}`}>
                  <FileText className="h-3 w-3" />
                  <span className="truncate max-w-[180px]">{a.filename}</span>
                  <button
                    onClick={() => setAttachments((prev) => prev.filter((x) => x.id !== a.id))}
                    data-testid={`compose-attachment-remove-${a.id}`}
                    className={`h-5 w-5 rounded flex items-center justify-center ${dark ? "hover:bg-white/10" : "hover:bg-slate-200"}`}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
            </div>
          )}
          <input ref={fileRef} type="file" multiple onChange={(e) => onFileChosen(e.target.files)} data-testid="compose-file-input" className="hidden" />

          {((channel === "sms" && !settings?.smsConfigured) || (channel === "email" && !settings?.emailConfigured)) && (
            <div className="text-[11.5px] text-amber-500 flex items-center gap-1.5"><AlertTriangle className="h-3.5 w-3.5" /> {channel === "sms" ? "Twilio SMS" : "Email"} isn&apos;t configured yet — set it up {platformMode ? "in Platform Console → Twilio / Branding" : "in Company Settings"} to deliver.</div>
          )}
        </div>
        <div className={`px-5 py-4 border-t flex items-center justify-between gap-2 ${headerBorder}`}>
          <button
            onClick={() => fileRef.current?.click()}
            disabled={uploading || sending}
            data-testid="compose-attach-btn"
            title="Attach a document"
            className={`h-9 px-3 rounded-md border text-[12.5px] font-semibold flex items-center gap-1.5 disabled:opacity-40 ${cancelBtn}`}
          >
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Paperclip className="h-4 w-4" />}
            {uploading ? "Uploading" : "Attach"}
          </button>
          <div className="flex gap-2">
            <button onClick={onClose} className={`h-9 px-4 rounded-md border text-[13px] font-semibold ${cancelBtn}`}>Cancel</button>
            <button onClick={submit} disabled={(!body.trim() && attachments.length === 0) || sending} data-testid="compose-send" className="h-9 px-5 rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-40 flex items-center gap-1.5 text-[13px] font-semibold">
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
